import React, { useState, useEffect } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import axios from "axios";

const paymentModes = [
  { value: "CASH", label: "Cash" },
  { value: "BANK_TRANSFER", label: "Bank Transfer" },
  { value: "UPI", label: "UPI" },
  { value: "CARD", label: "Credit Card" },
  { value: "CHEQUE", label: "Cheque" },
];

const expenseCategories = [
  { value: "COMMISSION", label: "Commission" },
  { value: "VENDOR_PAYMENT", label: "Vendor Payment" },
  { value: "TAXI_CHARGES", label: "Taxi Charges" },
  { value: "FOOD_EXPENSE", label: "Food Expense" },
  { value: "MAINTENANCE", label: "Maintenance" },
  { value: "SUPPLIES", label: "Supplies" },
  { value: "MISCELLANEOUS", label: "Miscellaneous" },
];

const AddEditExpensePopup = ({ onSubmit, expenseData, onCancel }) => {
  const [formValues, setFormValues] = useState({
    amount: "",
    category: null,
    description: "",
    paymentMode: paymentModes[0], // Default to CASH
    referenceId: "",
    referenceType: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const isEditMode = !!expenseData;

  useEffect(() => {
    if (isEditMode && expenseData) {
      setFormValues({
        amount: expenseData.amount?.toString() || "",
        category: expenseCategories.find(cat => cat.value === expenseData.category) || null,
        description: expenseData.description || "",
        paymentMode: paymentModes.find(mode => mode.value === expenseData.payment_mode) || paymentModes[0],
        referenceId: expenseData.reference_id || "",
        referenceType: expenseData.reference_type || "",
      });
    }
  }, [expenseData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSelectChange = (name) => (selectedOption) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: selectedOption,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    let validationErrors = {};
    
    if (!formValues.amount || isNaN(parseFloat(formValues.amount)) || parseFloat(formValues.amount) <= 0) {
      validationErrors.amount = "Please enter a valid amount";
    }
    if (!formValues.category) {
      validationErrors.category = "Category is required";
    }
    if (!formValues.paymentMode) {
      validationErrors.paymentMode = "Payment mode is required";
    }
    
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }
  
    try {
      setLoading(true);
  
      const payload = {
        amount: parseFloat(formValues.amount),
        category: formValues.category.value,
        description: formValues.description,
        paymentMode: formValues.paymentMode.value,
        referenceId: formValues.referenceId,
        referenceType: formValues.referenceType,
      };
  
      if (isEditMode) {
        payload.expenseId = expenseData.transaction_id;
      }
  
      const endpoint = isEditMode
        ? `${apiUrl}/transactions/edit-expenses`
        : `${apiUrl}/transactions/add-expenses`;
  
      const response = await axios.post(endpoint, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
  
      toast.success(`Expense ${isEditMode ? 'updated' : 'added'} successfully!`);
      if (onSubmit) onSubmit(response.data);
    } catch (error) {
      toast.error(`Failed to ${isEditMode ? 'update' : 'add'} expense: ${error.response?.data?.message || 'Unknown error'}`);
      console.error(`Error ${isEditMode ? 'updating' : 'adding'} expense:`, error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className={`modal custom-modal ${isEditMode ? "edit-mode" : ""}`}
      tabIndex="-1" 
      role="dialog"
      style={{ 
        display: "block", // Always render but can be hidden by parent
        position: "fixed", 
        top: "-30px", 
        right: 0, 
      
      }}
    >
   <div
    className="modal-dialog modal-lg"
    style={{
      margin: 0,
      position: "fixed",
      right: 0,
      height: "100vh",
      width: "40%",
    }}
  >
      >
        <form onSubmit={handleSubmit} style={{ height: "100%" }}>
          <div className="modal-content" style={{ height: "100%", border: "none", borderRadius: 0 }}>
            <div className="modal-header">
              <h5 className="modal-title">{isEditMode ? "Edit Expense" : "Add Expense"}</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onCancel}
                aria-label="Close"
                style={{ 
                  background: "transparent", 
                  border: "none", 
                  fontSize: "1.5rem", 
                  cursor: "pointer" 
                }}
              />
            </div>

            <div className="modal-body" style={{ maxHeight: "calc(100vh - 80px)", overflowY: "auto" }}>
              <div className="mb-3">
                <label className="form-label">Amount</label>
                <input
                  type="number"
                  name="amount"
                  value={formValues.amount}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="Enter amount"
                />
                {errors.amount && <small className="text-danger">{errors.amount}</small>}
              </div>

              <div className="mb-3">
                <label className="form-label">Category</label>
                <Select
                  options={expenseCategories}
                  onChange={handleSelectChange("category")}
                  value={formValues.category}
                  isSearchable={true}
                />
                {errors.category && <small className="text-danger">{errors.category}</small>}
              </div>

              <div className="mb-3">
                <label className="form-label">Payment Mode</label>
                <Select
                  options={paymentModes}
                  onChange={handleSelectChange("paymentMode")}
                  value={formValues.paymentMode}
                  isSearchable={false}
                />
                {errors.paymentMode && <small className="text-danger">{errors.paymentMode}</small>}
              </div>

              <div className="mb-3">
                <label className="form-label">Description</label>
                <textarea
                  name="description"
                  value={formValues.description}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="Enter description (optional)"
                  rows="3"
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Reference ID</label>
                <input
                  type="text"
                  name="referenceId"
                  value={formValues.referenceId}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="Enter reference ID (optional)"
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Reference Type</label>
                <input
                  type="text"
                  name="referenceType"
                  value={formValues.referenceType}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="Enter reference type (optional)"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Saving..." : "Save"}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={onCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditExpensePopup;